import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { useFlag } from '@/services/backstage/Backstage';
import { Sketch } from '@uiw/react-color';
import { FC, useEffect, useRef, useState } from 'react';
import { FiCalendar, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import strings from '../admin.translations';
import { LeaveTypeRow } from './leaveTypes.types';

interface LeaveTypeEditCardProps {
  leaveType: LeaveTypeRow;
  mode?: 'create' | 'edit';
  onChange: (leaveType: LeaveTypeRow) => void;
  onCancel: () => void;
  onSave: () => void | Promise<void>;
  onDelete?: () => void;
  saving?: boolean;
}

export const LeaveTypeEditCard: FC<LeaveTypeEditCardProps> = ({
  leaveType,
  mode = 'edit',
  onChange,
  onCancel,
  onSave,
  onDelete,
  saving
}) => {
  const isDebug = useFlag('debug');

  const [backgroundColorPickerOpen, setBackgroundColorPickerOpen] = useState(false);
  const backgroundColorPickerRef = useRef<HTMLDivElement | null>(null);
  const isCreateMode = mode === 'create';
  const deactivateButtonText = leaveType.active ? strings.editLeaveType.deactivate : strings.editLeaveType.activate;
  const deactivateDescription = leaveType.active
    ? strings.editLeaveType.deactivateDescription
    : strings.editLeaveType.activateDescription;

  useEffect(() => {
    if (!backgroundColorPickerOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!backgroundColorPickerRef.current?.contains(event.target as Node)) {
        setBackgroundColorPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [backgroundColorPickerOpen]);

  return (
    <TSTitleCard
      title={
        isCreateMode
          ? strings.editLeaveType.createTitle
          : strings.editLeaveType.title(leaveType.displayName.label || leaveType.name.label)
      }
      icon={FiCalendar}
      className="w-full"
    >
      <div className="grid gap-4">
        <div>
          <label className="block text-sm font-medium text-primary-std mb-2">{strings.leaveTypeTable.name}</label>
          <input
            value={leaveType.name.label}
            onChange={event => onChange({ ...leaveType, name: { ...leaveType.name, label: event.target.value } })}
            className="w-full rounded-md border border-primary-std bg-primary-std px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-primary-std mb-2">
            {strings.leaveTypeTable.displayName} - {strings.editLeaveType.displayNameHelp}
          </label>
          <input
            value={leaveType.displayName.label}
            onChange={event =>
              onChange({ ...leaveType, displayName: { ...leaveType.displayName, label: event.target.value } })
            }
            className="w-full rounded-md border border-primary-std bg-primary-std px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-primary-std mb-2">{strings.editLeaveType.code}</label>
          <input
            value={leaveType.code}
            onChange={event => onChange({ ...leaveType, code: event.target.value })}
            className="w-full rounded-md border border-primary-std bg-primary-std px-3 py-2 text-sm"
          />
        </div>
        <div className="grid gap-3 md:grid-cols-[140px_140px]">
          <div>
            <label className="block text-sm font-medium text-primary-std mb-2">
              {strings.editLeaveType.backgroundColor}
            </label>
            <div className="relative" ref={backgroundColorPickerRef}>
              <button
                type="button"
                className="flex h-10 w-full items-center rounded-md border border-primary-std bg-primary-std px-1.5 text-sm"
                onClick={() => setBackgroundColorPickerOpen(current => !current)}
              >
                <span
                  className="h-7 w-[92px] rounded-sm border border-primary-std/20"
                  style={{ backgroundColor: leaveType.color }}
                />
                <span className="flex flex-1 items-center justify-center">
                  {backgroundColorPickerOpen ? (
                    <FiChevronUp className="h-4 w-4" />
                  ) : (
                    <FiChevronDown className="h-4 w-4" />
                  )}
                </span>
              </button>
              {backgroundColorPickerOpen && (
                <div className="absolute z-20 mt-2 rounded-md border border-primary-std bg-primary-std p-3 shadow-lg">
                  <Sketch
                    color={leaveType.color}
                    onChange={color => onChange({ ...leaveType, color: color.hex })}
                    width={260}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary-std mb-2">{strings.editLeaveType.textColor}</label>
            <select
              value={leaveType.textColor}
              onChange={event => onChange({ ...leaveType, textColor: event.target.value })}
              className="h-10 w-full rounded-md border border-primary-std bg-primary-std px-3 py-2 text-sm"
            >
              <option value="Black">{strings.editLeaveType.textColorBlack}</option>
              <option value="White">{strings.editLeaveType.textColorWhite}</option>
            </select>
          </div>
        </div>
        <div>
          <div className="block text-sm font-medium text-primary-std mb-2">{strings.editLeaveType.managerOnly}</div>
          <label className="flex items-center gap-2 text-sm text-secondary-std">
            <input
              type="checkbox"
              checked={Boolean(leaveType.managerOnly)}
              onChange={event => onChange({ ...leaveType, managerOnly: event.target.checked })}
            />
            <span>{strings.editLeaveType.managerOnlyDescription}</span>
          </label>
        </div>
        {!isCreateMode && (
          <div>
            <div className="block text-sm font-medium text-primary-std mb-2">
              {strings.editLeaveType.activationTitle}
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
              <Button
                secondary={leaveType.active}
                primary={!leaveType.active}
                tint
                className="w-full whitespace-nowrap md:w-max md:min-w-[9rem] md:flex-none"
                onClick={() => onChange({ ...leaveType, active: !leaveType.active })}
              >
                {deactivateButtonText}
              </Button>
              <p className="text-sm text-secondary-std md:flex md:min-h-12 md:items-center">{deactivateDescription}</p>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-3 pt-2 md:flex-row md:items-center md:justify-between">
          <div>
            {isDebug && !isCreateMode && onDelete ? (
              <Button danger onClick={onDelete} disabled={saving}>
                {strings.leaveTypeTable.delete}
              </Button>
            ) : null}
          </div>
          <div className="flex gap-3">
            <Button secondary tint onClick={onCancel} disabled={saving}>
              {strings.editLeaveType.cancel}
            </Button>
            <Button primary onClick={onSave} loading={saving} disabled={saving}>
              {strings.editLeaveType.save}
            </Button>
          </div>
        </div>
      </div>
    </TSTitleCard>
  );
};
